import type { Dispatch, FC, SetStateAction } from 'react'

import { Select, Input, Checkbox, NotificationBubble, StyledText, Icon } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import CellphoneIcon from 'mdi-react/CellphoneIcon'
import CellphoneKeyIcon from 'mdi-react/CellphoneKeyIcon'
import FinanceIcon from 'mdi-react/FinanceIcon'
import PlusIcon from 'mdi-react/PlusIcon'
import { useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useIsAdmin } from '@/hooks/access'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import useUser from '@/hooks/user'
import adminResources from '@/modules/admin/resources'
import { APP_ROUTES } from '@/util/app-routes'

import queries from '../queries'
import type { AssetStatusOriginal, UserTypeOriginal } from '../resources/assets/types'

import { ActionsDropdown } from './AssetLeased/ActionsDropdown'
import { useLeasedAssets } from './AssetsLeased.context'
import { useLeasedFeatureAccess } from './helpers'

type AssetTopProps = {
  isAnyAssetsLeased: boolean
  searchFilter: string
  isLoading: boolean
  isAllAssetsFromUserConcern: boolean
  isSendSMSAvailable: boolean
  setSearchFilter: (search: string) => void
  setIsStatsPopupOpen: Dispatch<SetStateAction<boolean>>
}

export const AssetTop: FC<AssetTopProps> = ({
  isAnyAssetsLeased,
  searchFilter,
  isLoading,
  isAllAssetsFromUserConcern,
  isSendSMSAvailable,
  setSearchFilter,
  setIsStatsPopupOpen,
}) => {
  const isAdmin = useIsAdmin()
  const navigate = useNavigate()
  const translate = useTranslation()
  const { selectedList, clearSelectedList, handleActionChange } = useLeasedAssets()
  const user = useUser()
  if (!user) throw new Error('No user found for Assets!')

  const [queryAssetOwnership, setQueryAssetOwnership] = useSearchParamsByKey('assetOwnership')
  const [queryLeaseReturn, setQueryLeaseReturn] = useSearchParamsByKey('leaseReturn')
  const [queryUserType, setQueryUserType] = useSearchParamsByKey('userType')
  const [queryAssetStatus, setQueryAssetStatus] = useSearchParamsByKey('assetStatus')
  const [queryEntireConcern, setQueryEntireConcern] = useSearchParamsByKey('entireConcern')
  const [queryAllAssets, setQueryAllAssets] = useSearchParamsByKey('allAssets')
  const [queryAssetAge] = useSearchParamsByKey('assetAge')
  const [queryAssetType] = useSearchParamsByKey('assetType')

  const showLeasedFeatures = queryAssetOwnership === 'LEASED' && useLeasedFeatureAccess(user.role)

  const { data: assetTypes } = useQuery(queries.adminAssetTypes(), adminResources.getAssetTypes)

  const statusOptions = [
    { value: 'all', label: translate('All asset statuses') },
    ...(assetTypes?.statuses.map(
      ({ status }: { id: number; status: AssetStatusOriginal; description: string }) => ({
        value: status,
        label: translate(`asset_status/${status}`),
      })
    ) || []),
  ]
  const userTypeOptions = [
    { value: 'all', label: translate('All user types') },
    ...(assetTypes?.userTypes.map(
      ({ type }: { id: number; type: UserTypeOriginal; description: string }) => ({
        value: type,
        label: translate(`asset_user_type/${type}`),
      })
    ) || []),
  ]

  function handleSelectOwnedAssetOwnership() {
    setQueryAssetOwnership(queryAssetOwnership === 'OWNED' ? '' : 'OWNED')
    setQueryLeaseReturn('')
    clearSelectedList()
  }

  function handleSelectLeasedAssetOwnership() {
    setQueryAssetOwnership(queryAssetOwnership === 'LEASED' ? '' : 'LEASED')
    setQueryLeaseReturn('')
    clearSelectedList()
  }

  function handleSelectLeaseReturn(daysAhead: string) {
    setQueryLeaseReturn(daysAhead === queryLeaseReturn ? ' ' : daysAhead)
    setQueryAssetOwnership('LEASED')
  }

  const onActionChange = (value: string | number) => {
    handleActionChange(value)
  }

  return (
    <>
      {/* Top */}
      <div className="flex flex-row justify-between gap-[0.7rem] max-[500px]:flex-col">
        <div className="flex flex-col gap-[0.7rem] md:flex-row md:items-center">
          {isAdmin && (
            <Button
              className="mx-0 my-0.5"
              Icon={<PlusIcon />}
              onClick={() => navigate(APP_ROUTES.HARDWARE_ASSETS_NEW)}
            >
              <Translation id="New asset" />
            </Button>
          )}
          <div className="flex flex-row gap-[0.7rem] max-[500px]:flex-col">
            <Button
              className={twJoin(
                'mx-0 my-0.5',
                queryAssetOwnership !== 'OWNED' && 'focus:bg-[#eee] focus:shadow-none'
              )}
              selected={queryAssetOwnership === 'OWNED'}
              onClick={handleSelectOwnedAssetOwnership}
            >
              <>
                <Icon
                  className={twJoin(queryAssetOwnership !== 'OWNED' && 'text-black')}
                  isWhiteLabeled={queryAssetOwnership === 'OWNED'}
                >
                  <CellphoneKeyIcon className="ml-0 mr-1 w-[1.4rem]" />
                </Icon>
                <StyledText colorMode={queryAssetOwnership === 'OWNED' ? undefined : 'black'}>
                  <Translation id="Owned" />
                </StyledText>
              </>
            </Button>

            <Button
              className={twJoin(
                'mx-0 my-0.5',
                queryAssetOwnership !== 'LEASED' && 'focus:bg-[#eee] focus:shadow-none'
              )}
              selected={queryAssetOwnership === 'LEASED'}
              disabled={queryAssetOwnership !== 'LEASED' && !isAnyAssetsLeased}
              onClick={handleSelectLeasedAssetOwnership}
            >
              <>
                <Icon
                  className={twJoin(queryAssetOwnership !== 'LEASED' && 'text-black')}
                  isWhiteLabeled={queryAssetOwnership === 'LEASED'}
                >
                  <CellphoneIcon className="ml-0 mr-1 w-[1.4rem]" />
                </Icon>
                <StyledText colorMode={queryAssetOwnership === 'LEASED' ? undefined : 'black'}>
                  <Translation id="Leased" />
                </StyledText>
              </>
            </Button>
          </div>
        </div>

        {queryAssetOwnership === 'LEASED' && (
          <div className="flex flex-col items-center justify-end gap-[0.7rem] md:flex-row">
            <Select
              id="lease-return"
              className="w-full min-w-[170px]"
              value={queryLeaseReturn || ''}
              options={[
                { value: '', label: translate('Lease return') },
                { value: '30', label: `30 ${translate('days')}` },
                { value: '60', label: `60 ${translate('days')}` },
                { value: '90', label: `90 ${translate('days')}` },
              ]}
              onChange={option => {
                clearSelectedList()
                handleSelectLeaseReturn(option?.value || '')
              }}
            />

            {showLeasedFeatures && (
              <ActionsDropdown
                placeholder="Action"
                isDisabled={selectedList.length === 0}
                isSendSMSAvailable={isSendSMSAvailable}
                onChange={onActionChange}
              />
            )}
          </div>
        )}
      </div>

      {/* Bottom */}
      <div className="table-actions">
        <div className="search">
          <Input
            id="admin-asset-search"
            type="text"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />
        </div>

        <div className="flex w-full flex-wrap items-center justify-between gap-[0.7rem] max-[500px]:justify-start max-[500px]:gap-y-[0.7rem] xl:flex-nowrap">
          <div className="flex flex-wrap items-center gap-[0.7rem] xl:flex-nowrap">
            <Select
              id="user-type"
              className="min-w-[170px]"
              value={queryUserType || 'all'}
              options={userTypeOptions}
              isLoading={isLoading}
              onChange={option => setQueryUserType(option?.value || '')}
            />
            <Select
              id="asset-status"
              className="min-w-[170px]"
              value={queryAssetStatus || 'all'}
              options={statusOptions}
              isLoading={isLoading}
              onChange={option => setQueryAssetStatus(option?.value || '')}
            />
            {!isAllAssetsFromUserConcern && (
              <Checkbox
                marginTop={0}
                label={<Translation id="All companies" />}
                checked={queryEntireConcern ? JSON.parse(queryEntireConcern) : false}
                onChange={() => {
                  const updatedShowHidden = queryEntireConcern
                    ? JSON.parse(queryEntireConcern)
                      ? 'false'
                      : 'true'
                    : 'true'
                  setQueryEntireConcern(updatedShowHidden)
                }}
                id="entire-concern"
              />
            )}

            <Checkbox
              id="show-hidden-input"
              marginTop={0}
              label={<Translation id="Show hidden" />}
              checked={queryAllAssets ? JSON.parse(queryAllAssets) : false}
              onChange={() => {
                const updatedShowHidden = queryAllAssets
                  ? JSON.parse(queryAllAssets)
                    ? 'false'
                    : 'true'
                  : 'true'
                setQueryAllAssets(updatedShowHidden)
              }}
            />
          </div>

          <Button
            className="m-0"
            Icon={<FinanceIcon />}
            onClick={() => setIsStatsPopupOpen((prev: boolean) => !prev)}
          >
            <Translation id="Stats" />
            {queryAssetAge && queryAssetType ? (
              <NotificationBubble className="sticky">2</NotificationBubble>
            ) : queryAssetAge || queryAssetType ? (
              <NotificationBubble className="sticky">1</NotificationBubble>
            ) : null}
          </Button>
        </div>
      </div>
    </>
  )
}
